export interface GetActiveCustomersRequest {
  sellerWorkspaceId: string;
  pageNo: number;
  pageSize: number;
  startDate: string;
  endDate: string;
}

export interface GetActiveCustomersResponse {
  activeUsers: ActiveCustomer[];
  pagination: Pagination;
}

export interface ActiveCustomer {
  phone: string;
  wabaId: string;
  workspaceId: string;
  inviteId: string;
  firstMessageOfMonth: string;
  lastMessageOfMonth: string;
  messageCount: number;
  customerName: string;
}

export interface Pagination {
  pageNo: number;
  pageSize: number;
  totalCount: number;
  startRecord: number;
  endRecord: number;
}

export interface AudienceRequest {
  sellerWorkspaceId: string;
  pageNo?: number; // default: 1
  pageSize?: number; // default: 20
  searchKey?: string;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC'; // default: "ASC"
  dontIncludePagination?: boolean;
  excludedAudience?: boolean;
  isActive?: 0 | 1; // default: true
  divisionIds?: string[]; // default: []
  customer_segment?: string[]; // default: []
  routes?: string[]; // default: []
  onboardingStatus?: string[]; // default: []
}
export interface AudienceResponse {
  customers: AudienceCustomer[];
  totalCount: number;
  startRecord: number;
  endRecord: number;
  status: string;
  statusCode: number;
}

export interface AudienceCustomer {
  id: string;
  name: string;
  mobile: string;
  leadStatus?: string;
  locations?: string[];
  toPay?: number;
  lastInvoicedDate?: string;
  isOnWhatsApp?: boolean;
  phone: string;
  senderUserId: string;
  senderWorkspaceId: string;
  invitedWorkspaceId: string;
  status: string;
  isActive: number;
  createdDate: string;
  updatedDate: string;
  gstin: string;
  wgstin: string;
  businessPAN: null;
  distributorCode: string;
  postalCode: string;
  physicalAddress: string;
  companyName: string;
  stateId: string;
  countryId: string;
  cityName: string;
  email: string;
  grossCreditLimit: number;
  creditLimitPeriod: number;
  telephone: string;
  CSTNumber: string;
  distributorChannel: string;
  stateName: string;
  stateCode: string;
  cityCode: string;
  shippingAddress: string;
  cfaCount: number;
  divisionCount: string;
  routes: { channelId: string; channelName: string; folderType: string }[];
  customer_segment: {
    channelId: string;
    channelName: string;
    folderType: string;
  }[];
  attributeValues: null;
}

export interface ExludeActiveCustomerRequest {
  sellerWorkspaceId: string;
  exclude: boolean;
  inviteIds: string[];
}

export interface CustomerDetailRequest {
  sellerWorkspaceId: string;
  inviteId: string;
  includeInactive: boolean;
  includeCFA?: boolean;
}

export interface DocumentRequestHub {
  workspaceId: string;
  documentType: string;
  entityId?: string;
  mimeType?: string;
  subType?: string;
  sequenceNo?: string | number;
  defaultQr?: string;
  teamMemberId?: string;
  userName?: string;
}

export interface CustomerDetailResponse {
  excludedAndBlockedBotAudience: {
    inviteId: string;
    isArchived: boolean;
  } | null;
  id: string;
  inviteId: string;
  name: string;
  sellerWorkspaceImage: string;
  email: string;
  companyName: string;
  gstin: string;
  businessPAN: string;
  distributorCode: string;
  postalCode: string;
  importSource: string;
  location: {
    lat: number;
    lng: number;
  };
  physicalAddress: { address1: string; address2: string };
  shippingAddress: {
    shippingAddress1: string;
    shippingAddress2: string;
    pincode: string;
    city: string;
    state: string;
    countryCode: string;
  };
  distributorChannel: string;
  CSTNumber: string;
  telephone: string;
  token: string;
  cityId: string;
  districtId: string;
  cityName: string;
  createdDate: string;
  updatedDate: string;
  grossCreditLimit: number;
  creditLimitPeriod: number;
  tags: any;
  fulfillmentLocationCode: string;
  countryId: string;
  stateId: string;
  languageCode: string;
  stateDetails?: {
    id: string;
    createdDate: string | null;
    updatedDate: string;
    name: string;
    stateCode: string;
    isoCode: string | null;
    gstStateCode: string;
  };
  cfa: Cfa[];
  invite: Invite;
  waConfig: {
    wabaId: string;
    whatsappPhoneNoId: string;
    whatsappPhoneNo: string;
    provider: string;
    isDefault: boolean;
  };
  workspace: {
    id: string;
    spaceName: string;
    location: string;
    address: string;
  };
  contacts: Contact[];
  priceLists: CustomerPriceList[];
  routes: {
    id: string;
    channelName: string;
    type: string;
    folderId: string;
  }[];
  segments: {
    id: string;
    channelName: string;
    type: string;
    folderId: string;
  }[];
  teamMembers: {
    id: string;
    name: string;
    customerId: string;
    mobile: string;
    userType: string;
    roles: string[];
  }[];
}

export interface Cfa {
  id: string;
  salesOrg: string;
  cfaCode: string;
  name: string;
  divisions: {
    id: string;
    name: string;
    code: string;
    isActive: boolean;
  }[];
}
type CommunicationPreference = 'Finance' | 'Campaign' | 'Order';

interface Contact {
  id: string;
  createdDate: string;
  updatedDate: string;
  email: string | null;
  mobile: string;
  inviteId: string;
  name: string;
  workspaceId: string;
  userId: string | null;
  communicationPreferences: CommunicationPreference[] | null;
  isActive: boolean;
  isArchive: boolean;
}

export interface Invite {
  id: string;
  phone: string;
  phone2: string;
  inviteType: string;
  userType: any;
  isActive: boolean;
  isArchive: boolean;
  senderUserId: string;
  invitedUserId: string;
  senderWorkspaceId: string;
  invitedWorkspaceId: string;
  status: string;
  createdDate: string;
  updatedDate: string;
}

export interface CustomerPriceList {
  id: string;
  createdDate: string;
  updatedDate: string;
  name: string;
  code: string;
  type: any;
  criteria: string;
  isActive: boolean;
  isArchived: boolean;
  startDate: string;
  endDate: string;
  channelId: number;
}

export interface CustomerListRequest {
  includeWorkspaceMembers?: number;
  includeCustomerGroupAssignments?: number;
  includeTeamCustomersMapping?: boolean;
  includeCFADivision?: any;
  isIncludeSummaryAndTags?: boolean;
  pageNo?: number;
  pageSize?: number;
  workspaceId: string;
  searchKey?: string;
  status?: boolean;
  isActive?: boolean;
}

export interface UpdateCustomerActiveStatusRequest {
  customerId: string;
  isActive: boolean;
}

export interface CustomerSearchListRequest {
  workspaceId: string;
  searchKey?: string;
  pageNo?: number;
  pageSize?: number;
  customerGroupIds?: string[];
  customerCode?: string;
  routes?: string[];
  customer_segment?: string[];
  inviteId?: any[];
  pinCodes?: string[];
  includeCustomerGroupAssignments?: number;
  includeWorkspaceMembers?: number;
  divisionIds?: string[];
  cfaIds?: string[];
  onboardingStatus?: string;
}

export interface CustomerListResponse {
  customers?: CustomerData[];
  totalCount: number | null;
  startRecord: number | null;
  endRecord: number | null;
}

export interface CustomerMetricsReportRequest {
  params: {
    sellerWorkspaceId: string;
  };
  body: {
    customerIds: string[];
    sellerworkspaceid: string;
  };
}

export interface CustomerMetricsReportItem {
  inviteid?: string;
  customerId?: string;
  customerid?: string;
  toPay?: number | string;
  topay?: number | string;
  outstandingamount?: number | string;
  lastInvoicedDate?: string;
  lastinvoiceddate?: string;
  lastInvoicedOn?: string;
  lastinvoicedon?: string;
}

interface LastPayment {
  id: string;
  createdDate: Date;
  updatedDate: Date;
  sellerWorkspaceId: string;
  buyerWorkspaceId: string;
  inviteId: string;
  paymentDate: Date;
  amountPaid: number;
  allocatedAmount: number;
  currencyCode: string;
  status: string;
  paymentType: string;
  paymentMode: string;
  paymentProvider?: any;
  proofOfPayment?: any;
  approvedBy?: any;
  createdBy: string;
  paymentRefCode: string;
  instrumentNumber?: any;
  instrumentDate?: any;
  bankName?: any;
  bankAccountNumber?: any;
  bankReference?: any;
  channel?: any;
  cardNumber?: any;
  cardNetwork?: any;
  cardType?: any;
  bankCode?: any;
  upiId?: any;
  narration: string;
  source: string;
  teamMemberId: string;
}

interface DueInvoice {
  id: string;
  createdDate: Date;
  updatedDate: Date;
  referenceInvoiceNumber: string;
  supTyp: string;
  invoiceDate: Date;
  dueDate: Date;
  invoiceAmount: number;
  paidAmount: number;
  irn: string;
  customerName: string;
  docType: string;
  gstin?: any;
  docNumber: string;
  status: string;
  invoiceStatus: string;
  workspaceId: string;
  inviteId: string;
  linkedWorkspacesId: string;
}

interface BankAccount {
  id: string;
  createdDate: string;
  updatedDate: string;
  workspaceId: string;
  accountNumber: string;
  accountHolderName: string;
  IFSCCode: string;
  bankName: string;
  bankCity: string;
  bankStateCode: string;
  bankCountryCode: string;
  utr: string;
  branch: string;
  micr: string;
  accessType: string;
  upiId: string;
  isUsedForSettlement: boolean;
  isUsedForCashBack: boolean;
  status: string;
}
export interface CustomerPartyAccountBook {
  overdueAmount: number;
  lastPayment: LastPayment;
  dueInvoice: DueInvoice;
  totalCredit?: number;
  totalDebit?: number;
  openingBalance?: number;
  outStandingBalance?: number;
}

export interface Profile {
  id: string;
}

interface Ledger {
  id: string;
  transactionDate: string;
  createdDate: string;
  updatedDate: string;
  sellerWorkspaceId: string;
  buyerWorkspaceId: string;
  inviteId: string;
  docNumber: string;
  narration: string;
  creditAmount: number;
  debitAmount: number;
  balance: number;
  transactionType: string;
}

export interface LedgerListResponse {
  partyAccountBook: Ledger[];
  totalPartyAccountBook: number;
  startRecord: number;
  endRecord: number;
  statusCode: number;
  responseStatus: string;
}

export interface Visits {
  id: string;
  createdDate: string;
  updatedDate: string;
  userId: string;
  customerId: string;
  lat: string;
  lng: string;
  visitedDateTime?: string;
}

export interface CustomerInvoicesEntity {
  id: string;
  createdDate: string;
  updatedDate: string;
  referenceInvoiceNumber: string;
  supTyp: string;
  invoiceDate: string;
  invoiceAmount: number;
  irn: string;
  customerName: string;
  customerPhoneNumber?: string;
  docType: string;
  docNumber: string;
  invoiceStatus: string;
  workspaceId: string;
  inviteId: string;
  linkedWorkspacesId: string;
  eInvoice?: null[] | null;
  payments: any[];
  isSelected?: boolean;
  paidAmount?: string;
  dueDate: string;
  pendingAmount?: string;
  amountCleared: number;
  status: string;
  outStandingAmount: 0;
}
export interface CustomerPaymentInvoices {
  invoices?: CustomerInvoicesEntity[] | null;
  totalInvoices: number;
  startRecord: number;
  endRecord: number;
  pageNo: number;
}

interface AccountDetails {
  virtualVpaId: string;
  vAccountId: string;
  inviteId: string;
  upi: string;
  accountNumber: string;
  ifsc: string;
  qrCode: string;
  statusCode: number;
  responseStatus: string;
}

export interface CustomerPaymentErrorDetails {
  orderAmount?: string;
  orderId?: string;
  paymentMode?: string;
  referenceId?: string;
  signature?: string;
  txMsg?: string;
  txStatus?: string;
  txTime?: string;
}

export interface CustomerPaymentsTransactionsEntity {
  id: string;
  transactionId: number;
  customerName: string;
  transactionDate: string;
  amountPaid: number;
  paymentType: string;
  paymentMode: string;
  paymentRefCode: string;
  instrumentNumber: string;
  instrumentDate: string;
  bankName: string;
  narration?: string;
  inviteId: string;
  workspaceId: string;
  status: string;
  createdDate: string;
  updatedDate: string;
  invoices?: string[];
  paymentInvoiceMap?: {
    invoice: CustomerInvoicesEntity;
    amountCleared: number;
  }[];
  teamMemberId: string;
  channel?: string;
  cardNumber?: string;
  cardNetwork?: string;
  cardType?: string;
  upiId?: string;
  bankAccountNumber?: string;
  bankCode?: string;
  paymentId?: string;
  cardCountry: string;
  collectedBy?: string;
  paymentDate?: string;
  errorDetails?: {
    responseCode: string;
    paymentResponseMsg: string;
    paymentResponse: CustomerPaymentErrorDetails;
  };
  bankReference?: string;
}

export interface PaymentTransactions {
  payments?: CustomerPaymentsTransactionsEntity[] | null;
  total: number;
  startRecord: number;
  endRecord: number;
  pageNo?: number;
}

export interface CustomerGroups {
  createdDate: string;
  groupRefCode: null | string;
  groupsHierarchyDimensionsId: string;
  heritage: null | string;
  hierarchyType: string;
  id: string;
  lineage: null | string;
  name: string;
  parentGroupId: string;
  updatedBy: null | string;
  updatedDate: string;
  workspaceId: string;
}

interface OrderCount {
  Approved: string;
  SubmittedByCustomer: string;
  Confirmed: string;
  AddingItems: string;
  WaitingForCNF: string;
  Created: number;
  [key: string]: string | number; // To allow any other string keys with string or number values
}

export interface AssignedTeamMember {
  id: string;
  name: string;
  customerId: string;
  mobile: string;
  userType: string;
  roles: string[];
}

export interface CustomerSegment {
  channelId: string;
  channelName: string;
  folderType: string;
  customerGroupType: string;
  id: string;
  name: string;
  parentId: string;
}

export interface Route {
  channelId: string;
  channelName: string;
  folderType: string;
  customerGroupType: string;
  id: string;
  name: string;
  parentId: string;
}

export interface CustomerData {
  telephone?: string;
  leadStatus?: string;
  locations?: string[];
  toPay?: number;
  lastInvoicedDate?: string;
  isOnWhatsApp?: boolean;
  invitedWorkspaceId?: string;
  teamMemberId?: string;
  id: string;
  phone?: string;
  senderUserId: string;
  senderWorkspaceId: string;
  status: string;
  createdDate: string;
  updatedDate: string;
  gstin?: string;
  businessPAN?: string;
  distributorCode: string;
  postalCode?: string;
  physicalAddress?: string;
  companyName: string;
  stateId?: string;
  countryId?: string;
  mobile?: string;
  cityName?: string;
  name?: string;
  email?: string;
  spaceName?: string;
  address: string;
  profilePic: string;
  wgstin?: string;
  workspaceMembersCount: number;
  bankAccount: BankAccount;
  profile: Profile;
  sellerWorkspaceId: string;
  inviteId: string;
  city: string;
  grossCreditLimit?: number;
  totalDebit: number;
  totalCredit: number;
  outStandingBalance: number;
  openingBalance: number;
  overdueAmount: number;
  partyAccountBook: CustomerPartyAccountBook;
  lastPayment: LastPayment;
  dueInvoice: DueInvoice;
  Ledger: LedgerListResponse;
  dlNumber: string | null;
  visits: Visits[];
  accountDetails: AccountDetails;
  Invoices: CustomerPaymentInvoices;
  Payments: PaymentTransactions;
  customerCode?: string;
  lastPaymentDate?: Date;
  dueDate?: Date;
  isSelected?: boolean;
  customerGroups?: CustomerGroups[];
  divisionCount?: number;
  isActive?: number;
  cartCount?: number;
  closingBalance?: number;
  orderCount: OrderCount;
  stateName?: string;
  isLoading?: boolean;
  teamCustomers?: AssignedTeamMember[];
  shippingAddress: string;
  customer_segment?: CustomerSegment[];
  routes?: Route[];
  teamMemberCount?: number;
  excludedAndBlockedBotAudience?: {
    inviteId: string;
    isArchived: boolean;
  } | null;
}
export interface TeamSearchRequest {
  sellerWorkspaceId?: string;
  workspaceId?: string;
  includePendingInvites?: boolean;
  includeInactive?: boolean;
  includeTeamCustomersMapping?: boolean;
  searchKey?: string;
  page?: number;
  pageSize?: number;
  userType?: UserType[];
  cfaIds?: string[];
}

export enum UserType {
  CFA = 'CFA',
  SALES = 'Sales',
  MEMBER = 'M',
  OWNER = 'TeamOwner',
}

enum InviteStatus {
  ACCEPT = 'A',
  PENDING = 'P',
  NOT_INVITED = 'N',
}
export interface Cfas {
  id: string;
  createdDate: string;
  updatedDate: string;
  isDefault: boolean;
  supCode: any;
  isActive: boolean;
  startDate: any;
  endDate: any;
  workspaceId: string;
  teamMemberId: string;
  supplierName: string;
  fulfillmentLocationId: string;
}
export interface Division_ {
  name: string;
  headDivisionId: string;
  id: string;
  createdDate: any;
  updatedDate: any;
  code: string;
  stateId: null | any;
  isDefault: boolean;
  isActive: boolean;
}

export interface TeamMember {
  mobile: string;
  name: string;
  employeeCode: string;
  userId: string;
  userRole: string;
  id: string;
  inviteStatus: InviteStatus;
  cityName: string;
  profilePic: any;
  address: string;
  email: string;
  inviteType: string;
  role: string;
  isActive: boolean;
  isArchive: boolean;
  createdDate: string;
  updatedDate: string;
  userType: UserType;
  senderWorkspace: {
    id: string;
    spaceName: string;
    userName: string;
    profilePic: string;
    companyName: string;
  };
  teamMemberRole: {
    id: string;
    title: string;
    slug: string;
  }[];
  cfas: Cfas[];
  divisions: Division_[];
  teamCustomers?: number;
}

export interface TeamSearchResponse {
  data: TeamMember[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

export interface CfaRequest {
  workspaceId: string;
  pageNo?: number;
  pageSize?: number;
  searchKey?: string;
  sortDirection?: string;
  eitherOr?: boolean;
  sortBy?: string;
  isActive?: boolean;
  stateId?: string[];
  pinCodes?: string[];
  isNoCache?: boolean;
  includeDivisionData?: boolean;
  disablePagination?: boolean;
  isDefault?: boolean;
}

export interface CfaResponse {
  totalCount: number;
  startRecord: number;
  endRecord: number;
  totalRecords: number;
  cfas: CfaData[];
  status: string;
  statusCode: number;
}

export interface CfaData {
  id: string;
  supplierName: string;
  fulfillmentLocationCode: string;
  isDefault?: boolean;
  workspaceId?: string;
  stateId?: string;
  country?: string;
  physicalAddress?: {
    address1?: string;
  };
  cfaAddress?: string;
  GSTNumber?: string;
  PAN?: string;
  stateName?: string;
  stateCode?: string;
  postalCode?: string;
  numberOfCustomers?: any;
  numberOfDivisions?: any;
  phone?: string;
  email?: string;
  cityName?: string;
  stateDetails?: {
    id: string;
    createdDate: string;
    updatedDate: string;
    name: string;
    stateCode: string;
    isoCode: string;
    gstStateCode: string;
  };
  isActive?: boolean;
}

export interface State {
  id?: string;
  name: string;
  stateCode: null | string;
  isoCode?: null | string;
  gstStatecode: string;
  createdDate?: string | null;
  updatedDate?: string;
}

interface StateDetails {
  id: string;
  createdDate: string | null;
  updatedDate: string;
  name: string;
  stateCode: string;
  isoCode: string | null;
  gstStateCode: string;
}

export interface CreateCustomerShippingAddress {
  shippingAddress1?: string;
  shippingAddress2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  countryCode: string;
}

export interface CreateCustomerPhysicalAddress {
  address1?: string;
  address2?: string;
}

export interface CreateCustomerDetails {
  name?: string;
  companyName?: string;
  telephone?: string;
  email?: string;
  inviteId?: string;
  CSTNumber?: string;
  gstin?: string;
  dlNumber?: string;
  dlExpiry?: string;
  distributorChannel?: string;
  grossCreditLimit?: number;
  creditLimitPeriod?: number;
  shippingAddress?: CreateCustomerShippingAddress;
  physicalAddress?: CreateCustomerPhysicalAddress;
  postalCode?: string;
  stateDetails?: StateDetails;
  stateCode?: string;
  countryCode?: string;
  countryId?: string;
  cityName?: string;
  city?: string;
  customerCode?: string;
  distributorCode?: string;
  id?: string;
  priceListCriteria?: string;
  languageCode?: string;
  importSource?: string;
  location?: [number, number];
}

export interface CreateCustomerCfa {
  cfaCode?: string;
  salesOrg?: string;
  divisionCodes?: CreateCustomerDivisionCode[];
}

export interface CreateCustomerDivisionCode {
  code?: string;
  isActive?: boolean;
}

export interface CreateCustomerRequest {
  phone: string;
  customerDetails: CreateCustomerDetails;
  senderWorkspaceId?: string;
  inviteId?: string;
  phone2?: string;
  companyCode?: string;
  priceListCriteria?: string;
  isActive?: string;
  cfa?: CreateCustomerCfa[];
  location?: number[];
  priceListCode?: string;
  customAttributes?: Record<string, string | string[] | number>;
  isArchive?: boolean;
  isSelfRegister?: boolean;
}

interface CFARecord {
  divisionId: string;
  fulfillmentLocationId: string;
  isActive: boolean;
}

export interface NewCustomerDetails {
  name: string;
  companyName: string;
  telephone: string;
  customerCode: string;

  email?: string;
  CSTNumber?: string;
  gstin?: string;
  physicalAddress?: {
    address1: string;
  };
  postalCode?: string;
  languageCode?: string;
  countryCode?: string;
  cityName?: string;
  grossCreditLimit?: number;
  creditLimitPeriod?: number;
}

export interface CreateCustomerResponse {
  statusCode: number;
  message: string;
  data: {
    isExisting: boolean;
    senderWorkspaceId: string;
    phone: string;
    customerDetails: NewCustomerDetails;
    senderUserId: string;
    cfaWithId: CFARecord[];
    distributorCode: string;
    email: string;
    inviteId: string;
    message?: string;
  };
}

export interface DivisionRequest {
  workspaceId: string;
  id?: string;
  searchKey?: string;
  pageNo?: number;
  pageSize?: number;
  sortDirection?: string;
  sortBy?: string;
  headDivisionCode?: string;
  includeHeadDivision?: boolean;
  isDefault?: boolean;
}

export interface DivisionResponse {
  divisions: Divisions[];
  status: string;
  statusCode: number;
  startRecord?: number;
  endRecord?: number;
  totalRecords?: number;
}

export interface Divisions {
  isActive?: boolean;
  id: string;
  name: string;
  code: string;
  workspaceId?: string;
  isDefault?: boolean;
  createdDate?: string | null;
  updatedDate?: string | null;
  headDivisionId?: any;
  stateId?: any;
  division?: DivisionDetail;
  numberOfCustomer?: number;
  state?: DivisionState;
  headDivision?: HeadDivisionItem;
}

export interface DivisionDetail {
  id: string;
  createdDate: string;
  updatedDate: string;
  name: string;
  code: string;
  workspaceId: string;
  isDefault: boolean;
  isActive: boolean;
  headDivisionId: string;
  stateId: any;
}

export interface DivisionState {
  id: string;
  name: string;
  stateCode: null | string;
  isoCode: null | string;
  gstStatecode: string;
  createdDate?: string | null;
  updatedDate?: string;
}

interface HeadDivisionItem {
  code: string;
  createdDate: Date | null;
  id: string;
  isActive: boolean;
  isDefault: boolean;
  name: string;
  updatedDate: string; // Assuming this is a date string
  workspaceId: string;
}

export interface AssignedDivision {
  id: string;
  name: string;
  code: string;
}

export interface SelectedCfa {
  id: string;
  cfaName: string;
  cfaCode: string;
}

export interface NewCustomerPayload {
  mobileNumber: string;
  customerName: string;
  firmName: string;
  customerCode: string;
  emailId?: string;
  address?: string;
  isSameAddress?: boolean;
  location?: [string, string];
}

export interface CustomerFiltersResponse {
  routes?: CustomerGroupFilter[];
  customerSegment?: CustomerGroupFilter[];
  divisions?: {
    id: string;
    name: string;
  }[];
  cfa?: CfaData[];
  dynamicSegment?: CustomerGroupFilter[];
  customerLifeCycleGroups?: CustomerGroupFilter[];
}

export interface CustomerGroupFilter {
  id: string;
  name: string;
  customerGroups: CustomerGroup[];
  subFolders: CustomerGroupFilter[];
}

interface CustomerGroup {
  id: string;
  name: string;
  description: string;
  workspaceId: string;
  customerGroupType: string;
  parentId: string;
  customerCount: number;
  emojiId: string;
}

export interface CustomersReportResponse {
  inviteId: string;
  customerName?: string;
  grossCreditLimit?: string;
  creditLimitPeriod?: string;
  routes?: string;
  segments?: string;
  outstandingAmount?: string;
  latestInvoiceAmount?: string;
  latestInvoiceDate?: string;
  latestInvoiceNumber?: string;
  latestInvoiceId?: string;
  latestPaymentAmount?: string;
  latestPaymentNumber?: string;
  latestPaymentId?: string;
  latestOrderAmount?: string;
  latestOrderDate?: string;
  latestOrderNumber?: string;
  latestOrderId?: string;
  latestOrderCreatedBy?: string;
  latestLedgerUpdatedAt?: string;
  latestCheckinDate?: string;
}

export interface CustomersReportRequest {
  params: {
    sellerWorkspaceId: string;
  };
  body: {
    customerIds: string[];
    sellerworkspaceid: string;
  };
}

export interface ParseCustomerResponse {
  companyName?: string;
  name?: string;
  mobile_number_1?: number;
  mobile_number_2?: number;
  email?: string;
  address?: string;
  customer_code?: string;
}
